import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getDefaultGuidesHome } from '../../utils/vaGuidesHome';

function emptySection(order = 0) {
  return {
    title: 'New section',
    highlight: '',
    stepsLabel: `Step ${order + 1}`,
    order,
    steps: []
  };
}

function emptyHomeStep(stepNum = 1) {
  return {
    step: stepNum,
    title: '',
    summary: '',
    important: '',
    categoryHint: '',
    portalPath: '',
    portalLabel: '',
    guideId: null,
    guideLabel: '',
    linkedGuides: []
  };
}

function emptyLinkedGuide() {
  return { label: '', guideId: null };
}

function ListEditor({ label, items, onChange, placeholder }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
        <button
          type="button"
          onClick={() => onChange([...(items || []), ''])}
          className="text-xs text-indigo-600 hover:underline"
        >
          + Add line
        </button>
      </div>
      <div className="space-y-2">
        {(items || []).map((line, idx) => (
          <div key={`${label}-${idx}`} className="flex gap-2">
            <textarea
              rows={2}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              placeholder={placeholder}
              value={line}
              onChange={(e) => {
                const next = [...items];
                next[idx] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-red-600 hover:text-red-700 p-1"
              aria-label="Remove line"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuideSelect({ value, guides, onChange, allowEmpty = true }) {
  return (
    <select
      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
    >
      {allowEmpty && <option value="">— No guide linked —</option>}
      {guides.map((g) => (
        <option key={g._id} value={String(g._id)}>
          {g.title || 'Untitled guide'}
          {g.published === false ? ' (draft)' : ''}
        </option>
      ))}
    </select>
  );
}

export default function VAGuidesHomeEditor({ guidesHome, setGuidesHome, publishedGuides }) {
  const home = guidesHome || getDefaultGuidesHome();

  const updateHome = (patch) => setGuidesHome({ ...home, ...patch });

  const updateSection = (sectionIdx, patch) => {
    const sections = [...(home.sections || [])];
    sections[sectionIdx] = { ...sections[sectionIdx], ...patch };
    updateHome({ sections });
  };

  const updateStep = (sectionIdx, stepIdx, patch) => {
    const sections = [...(home.sections || [])];
    const steps = [...(sections[sectionIdx].steps || [])];
    steps[stepIdx] = { ...steps[stepIdx], ...patch };
    sections[sectionIdx] = { ...sections[sectionIdx], steps };
    updateHome({ sections });
  };

  const updateLinkedGuide = (sectionIdx, stepIdx, linkIdx, patch) => {
    const sections = [...(home.sections || [])];
    const steps = [...(sections[sectionIdx].steps || [])];
    const linkedGuides = [...(steps[stepIdx].linkedGuides || [])];
    linkedGuides[linkIdx] = { ...linkedGuides[linkIdx], ...patch };
    steps[stepIdx] = { ...steps[stepIdx], linkedGuides };
    sections[sectionIdx] = { ...sections[sectionIdx], steps };
    updateHome({ sections });
  };

  const addSection = () => {
    const sections = [...(home.sections || []), emptySection(home.sections?.length || 0)];
    updateHome({ sections });
  };

  const removeSection = (sectionIdx) => {
    if (!window.confirm('Delete this section and all its steps?')) return;
    updateHome({ sections: (home.sections || []).filter((_, i) => i !== sectionIdx) });
  };

  const addStep = (sectionIdx) => {
    const steps = [...(home.sections[sectionIdx].steps || []), emptyHomeStep((home.sections[sectionIdx].steps?.length || 0) + 1)];
    updateSection(sectionIdx, { steps });
  };

  const removeStep = (sectionIdx, stepIdx) => {
    const steps = (home.sections[sectionIdx].steps || []).filter((_, i) => i !== stepIdx);
    updateSection(sectionIdx, { steps });
  };

  const addLinkedGuide = (sectionIdx, stepIdx) => {
    const steps = [...(home.sections[sectionIdx].steps || [])];
    const linkedGuides = [...(steps[stepIdx].linkedGuides || []), emptyLinkedGuide()];
    steps[stepIdx] = { ...steps[stepIdx], linkedGuides };
    updateSection(sectionIdx, { steps });
  };

  const removeLinkedGuide = (sectionIdx, stepIdx, linkIdx) => {
    const steps = [...(home.sections[sectionIdx].steps || [])];
    const linkedGuides = (steps[stepIdx].linkedGuides || []).filter((_, i) => i !== linkIdx);
    steps[stepIdx] = { ...steps[stepIdx], linkedGuides };
    updateSection(sectionIdx, { steps });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Guides home page</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure the VA Guides landing page: section headers, onboarding steps, and linked guides.
        </p>
      </div>

      <div className="space-y-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Intro banner</p>
        <input
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
          placeholder="Title"
          value={home.intro?.title || ''}
          onChange={(e) => updateHome({ intro: { ...home.intro, title: e.target.value } })}
        />
        <input
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
          placeholder="Subtitle"
          value={home.intro?.subtitle || ''}
          onChange={(e) => updateHome({ intro: { ...home.intro, subtitle: e.target.value } })}
        />
        <textarea
          rows={2}
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
          placeholder="Note (shown in blue)"
          value={home.intro?.note || ''}
          onChange={(e) => updateHome({ intro: { ...home.intro, note: e.target.value } })}
        />
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Setup sections</p>
          <button type="button" onClick={addSection} className="text-sm text-indigo-600 hover:underline inline-flex items-center gap-1">
            <PlusIcon className="w-4 h-4" />
            Add section
          </button>
        </div>

        {(home.sections || []).map((section, sectionIdx) => (
          <div key={section._id || `section-${sectionIdx}`} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
            <div className="flex justify-between items-start gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Section {sectionIdx + 1}</p>
              <button type="button" onClick={() => removeSection(sectionIdx)} className="text-xs text-red-600 hover:underline">
                Delete section
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <input
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                placeholder="Section title (e.g. Setup accounts)"
                value={section.title || ''}
                onChange={(e) => updateSection(sectionIdx, { title: e.target.value })}
              />
              <input
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                placeholder="Highlight (e.g. Finish this for all accounts first!)"
                value={section.highlight || ''}
                onChange={(e) => updateSection(sectionIdx, { highlight: e.target.value })}
              />
              <input
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                placeholder="Steps label (e.g. Step 1)"
                value={section.stepsLabel || ''}
                onChange={(e) => updateSection(sectionIdx, { stepsLabel: e.target.value })}
              />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Steps in this section</p>
                <button type="button" onClick={() => addStep(sectionIdx)} className="text-xs text-indigo-600 hover:underline">
                  + Add step
                </button>
              </div>

              {(section.steps || []).map((step, stepIdx) => (
                <div key={step._id || `step-${stepIdx}`} className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3 bg-gray-50/50 dark:bg-gray-900/30">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold text-gray-500">Step {step.step ?? stepIdx + 1}</span>
                    <button type="button" onClick={() => removeStep(sectionIdx, stepIdx)} className="text-xs text-red-600 hover:underline">
                      Remove step
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input
                      type="number"
                      min={1}
                      className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                      placeholder="Step number"
                      value={step.step ?? stepIdx + 1}
                      onChange={(e) => updateStep(sectionIdx, stepIdx, { step: parseInt(e.target.value, 10) || 1 })}
                    />
                    <input
                      className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                      placeholder="Category hint (e.g. X Interface)"
                      value={step.categoryHint || ''}
                      onChange={(e) => updateStep(sectionIdx, stepIdx, { categoryHint: e.target.value })}
                    />
                  </div>
                  <input
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="Step title"
                    value={step.title || ''}
                    onChange={(e) => updateStep(sectionIdx, stepIdx, { title: e.target.value })}
                  />
                  <textarea
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="Summary"
                    value={step.summary || ''}
                    onChange={(e) => updateStep(sectionIdx, stepIdx, { summary: e.target.value })}
                  />
                  <input
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="Important warning (optional)"
                    value={step.important || ''}
                    onChange={(e) => updateStep(sectionIdx, stepIdx, { important: e.target.value })}
                  />

                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2 bg-white dark:bg-gray-800">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Main guide button (optional)</p>
                    <input
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                      placeholder="Button label"
                      value={step.guideLabel || ''}
                      onChange={(e) => updateStep(sectionIdx, stepIdx, { guideLabel: e.target.value })}
                    />
                    <GuideSelect
                      value={step.guideId}
                      guides={publishedGuides}
                      onChange={(guideId) => updateStep(sectionIdx, stepIdx, { guideId })}
                    />
                  </div>

                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3 bg-white dark:bg-gray-800">
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Linked guides (Open guide buttons)</p>
                      <button type="button" onClick={() => addLinkedGuide(sectionIdx, stepIdx)} className="text-xs text-indigo-600 hover:underline">
                        + Add guide link
                      </button>
                    </div>
                    {(step.linkedGuides || []).length === 0 && (
                      <p className="text-xs text-gray-500">No guide links yet. Add buttons like “Open privacy settings”.</p>
                    )}
                    {(step.linkedGuides || []).map((link, linkIdx) => (
                      <div key={link._id || `link-${linkIdx}`} className="flex flex-col sm:flex-row gap-2">
                        <input
                          className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                          placeholder="Button label"
                          value={link.label || ''}
                          onChange={(e) => updateLinkedGuide(sectionIdx, stepIdx, linkIdx, { label: e.target.value })}
                        />
                        <div className="flex-1">
                          <GuideSelect
                            value={link.guideId}
                            guides={publishedGuides}
                            onChange={(guideId) => updateLinkedGuide(sectionIdx, stepIdx, linkIdx, { guideId })}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLinkedGuide(sectionIdx, stepIdx, linkIdx)}
                          className="text-red-600 hover:text-red-700 p-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3 rounded-xl border border-amber-200 dark:border-amber-800 p-4 bg-amber-50/50 dark:bg-amber-950/20">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Manager gate warning</p>
        <input
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
          placeholder="Title"
          value={home.managerGate?.title || ''}
          onChange={(e) => updateHome({ managerGate: { ...home.managerGate, title: e.target.value } })}
        />
        <textarea
          rows={3}
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
          placeholder="Message"
          value={home.managerGate?.message || ''}
          onChange={(e) => updateHome({ managerGate: { ...home.managerGate, message: e.target.value } })}
        />
      </div>

      <div className="space-y-4 rounded-xl border border-emerald-200 dark:border-emerald-800 p-4">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily work section</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            placeholder="Title"
            value={home.dailyWork?.title || ''}
            onChange={(e) => updateHome({ dailyWork: { ...home.dailyWork, title: e.target.value } })}
          />
          <input
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            placeholder="Subtitle"
            value={home.dailyWork?.subtitle || ''}
            onChange={(e) => updateHome({ dailyWork: { ...home.dailyWork, subtitle: e.target.value } })}
          />
        </div>
        <ListEditor
          label="Overview bullets"
          items={home.dailyWork?.overview || []}
          onChange={(overview) => updateHome({ dailyWork: { ...home.dailyWork, overview } })}
          placeholder="Overview line"
        />
        <ListEditor
          label="Important rules"
          items={home.dailyWork?.rules || []}
          onChange={(rules) => updateHome({ dailyWork: { ...home.dailyWork, rules } })}
          placeholder="Rule"
        />
        <ListEditor
          label="Daily following process"
          items={home.dailyWork?.process || []}
          onChange={(process) => updateHome({ dailyWork: { ...home.dailyWork, process } })}
          placeholder="Process step"
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            placeholder="Target label"
            value={home.dailyWork?.target?.label || ''}
            onChange={(e) => updateHome({
              dailyWork: {
                ...home.dailyWork,
                target: { ...home.dailyWork?.target, label: e.target.value }
              }
            })}
          />
          <input
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            placeholder="Target math"
            value={home.dailyWork?.target?.math || ''}
            onChange={(e) => updateHome({
              dailyWork: {
                ...home.dailyWork,
                target: { ...home.dailyWork?.target, math: e.target.value }
              }
            })}
          />
        </div>
        <ListEditor
          label="Tips"
          items={home.dailyWork?.tips || []}
          onChange={(tips) => updateHome({ dailyWork: { ...home.dailyWork, tips } })}
          placeholder="Tip"
        />
      </div>
    </div>
  );
}

export function serializeGuidesHomeForSave(home) {
  if (!home) return null;
  return {
    intro: {
      title: home.intro?.title || '',
      subtitle: home.intro?.subtitle || '',
      note: home.intro?.note || ''
    },
    sections: (home.sections || []).map((section, i) => ({
      _id: section._id,
      title: section.title || '',
      highlight: section.highlight || '',
      stepsLabel: section.stepsLabel || '',
      order: i,
      steps: (section.steps || []).map((step, j) => ({
        _id: step._id,
        step: step.step ?? j + 1,
        title: step.title || '',
        summary: step.summary || '',
        important: step.important || '',
        categoryHint: step.categoryHint || '',
        portalPath: step.portalPath || '',
        portalLabel: step.portalLabel || '',
        guideId: step.guideId ? String(step.guideId) : null,
        guideLabel: step.guideLabel || '',
        linkedGuides: (step.linkedGuides || []).map((lg) => ({
          _id: lg._id,
          label: lg.label || '',
          guideId: lg.guideId ? String(lg.guideId) : null
        }))
      }))
    })),
    managerGate: {
      title: home.managerGate?.title || '',
      message: home.managerGate?.message || ''
    },
    dailyWork: {
      title: home.dailyWork?.title || '',
      subtitle: home.dailyWork?.subtitle || '',
      overview: (home.dailyWork?.overview || []).filter(Boolean),
      rules: (home.dailyWork?.rules || []).filter(Boolean),
      process: (home.dailyWork?.process || []).filter(Boolean),
      target: {
        label: home.dailyWork?.target?.label || '',
        math: home.dailyWork?.target?.math || ''
      },
      tips: (home.dailyWork?.tips || []).filter(Boolean)
    }
  };
}
